import React, { useState, useMemo, ReactElement } from 'react'
import { Collapse, Icon, Tooltip } from '@material-ui/core'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import { Flex } from 'components/common/styles'
import { ReactComponent as AttachmentIcon } from 'assets/img/attachment-icon.svg'
import { ReactComponent as DeleteJobIcon } from 'assets/img/delete-icon-redesign.svg'
import {
  getFormattedDate,
  getTimeFormat,
  getShortDateFormat,
} from 'helpers/dates'
import { MY_JOBS } from 'constants/appRouting'
import { DELETION_REASON_FIELDS } from 'routes/App/JobsAdmin/Jobs/JobWorkflowForm/constants'
import {
  DAILY,
  DEFAULT_JOB_ASSIGNEES_TMP_DATA,
  FILTER_TYPE_ASSIGNED_USER,
  MONTHLY,
  WEEKLY,
} from 'constants/managementUI'
import {
  deleteJob,
  updateJobAssignees,
  restoreJob,
  restoreJobSeries,
} from 'reducers/managementUI/reducer'
import { STATUS_NOT_STARTED } from 'helpers/jobs'
import { UnitInsideTree, UnitTree } from 'types/common'
import UsersListComponent from 'components/common/UsersList/UsersList'
import withPromise from 'helpers/withPromise'
import {
  Replay as ReopenIcon,
  ReplyAll as ReopenAllIcon,
} from '@material-ui/icons'
import {
  Label,
  NameCol,
  StyledUnitsCol,
  SingleJob as SingleJobStyles,
  IconHolder,
  OtherCol,
  TasksCompletedCol,
  RecurrenceCol,
  MediaCol,
  CreatedByCol,
  ManagementUICol,
  StatusIndicatorCol,
  ConfigureCol,
  MUIMenu,
  ContextMenuItem,
  ContextMenuItemSecondary,
  OverdueCol,
} from '../../ManagementUI.styles'
import { Data, SingleJobProps, TmpData } from './SingleJob.types'
import {
  CollapseIconWrapper,
  GroupName,
  JobPlaceholder,
  MoreOptionsIcon,
  TaskList,
  OverdueLabel,
  JobsWrapper,
} from './SingleJob.styles'
import SingleTask from '../SingleTask/SingleTask'
import PreviewInputValues from '../PreviewInputValues/PreviewInputValues'
import messages from '../../translate'
import { Task } from '../../ManagementUI.types'

export const SingleJobComponent = ({
  intl: { formatMessage },
  jobData: {
    id: jobId,
    number: jobNumber,
    title,
    repeat_until_date_time,
    end_date_time,
    state: jobState,
    overdue,
    tasks,
    date_deleted,
    assigned_users,
    assigned_user_groups,
    created_by,
    created_by_trigger_rule,
    modified_by,
    media,
    tasks_completed_count,
    tasks_total_count,
    job_series_id,
    preview_inputs: previewInputs,
    site: { name: siteName },
    locuses_and_groups,
    needs_deletion_reason,
    deletion_reasons,
    deletion_reason_type,
    needs_locus,
    needs_locus_group,
    schedule,
    start_date_time,
    created_date_time,
    completed_date_time,
    is_future,
  },
  getStatusIcon,
  assigneeColDisplay,
  recurrenceColDisplay,
  mediaColDisplay,
  jobIdColDisplay,
  createdByColDisplay,
  modifiedByColDisplay,
  tasksCompletedColDisplay,
  startDateColDisplay,
  endDateColDisplay,
  createdDateColDisplay,
  completedDateColDisplay,
  dueDateColDisplay,
  unitsColDisplay,
  updateJobAssignees,
  setIsDialogOpened,
  setConfirmDialogSettings,
  setJobContextMenuPosition,
  allowedToDeleteJobs,
  allowedToDeleteJobSeries,
  deleteJob,
  restoreJob,
  restoreJobSeries,
  licensee: { usaDateFormat, usaTimeFormat },
  alwaysVisible,
  isSchedulingTab,
  fetchManagementUITable,
  isLoading,
  isEverythingDeletedTab,
  canRecoverDeletedJobs,
}: SingleJobProps) => {
  const [isOpened, setIsOpened] = useState(false)
  const [
    jobContextMenuAnchor,
    setJobContextMenuAnchor,
  ] = useState<Element | null>(null)
  const [updateJobAssigneesTmpData, setUpdateJobAssigneesTmpData] = useState<
    Data
  >(DEFAULT_JOB_ASSIGNEES_TMP_DATA as Data)
  const isCompleted = useMemo(() => jobState === 'completed', [jobState])
  const shortDateDisplay = useMemo(() => getShortDateFormat(usaDateFormat), [
    usaDateFormat,
  ])

  const timeDisplay = useMemo(
    () => getTimeFormat(usaTimeFormat).replace(/\s+/g, ''),
    [usaTimeFormat],
  )

  const isAllowedToChange = useMemo(
    () => !is_future && !isCompleted && !isSchedulingTab,
    [is_future, isCompleted, isSchedulingTab],
  )

  const onOpenJobDialog = (data: {
    popupTitle: string
    popupMessage: ReactElement
    popupAction: (value: string) => () => void
    needsDeletionReason?: boolean
    confirmText?: string
    deletionReasonType?: DELETION_REASON_FIELDS
    deletionReasons?: string[]
  }) => {
    setIsDialogOpened(true)
    setJobContextMenuAnchor(null)
    setConfirmDialogSettings(data)
  }

  const onConfirmDeleteJob = (
    series: boolean,
    successMessage: string,
    errorMessage: string,
    deletionReasonFieldValue: string,
  ) => {
    setIsDialogOpened(false)
    withPromise(deleteJob, {
      id: jobId,
      job_series_id,
      series,
      successMessage,
      errorMessage,
      data: needs_deletion_reason
        ? {
            deletion_reason: deletionReasonFieldValue,
          }
        : undefined,
      isSchedulingTab,
    }).then(() => {
      fetchManagementUITable()
    })
  }

  const onConfirmRestoreJob = (
    successMessage: string,
    errorMessage: string,
  ) => {
    setIsDialogOpened(false)
    withPromise(restoreJob, {
      id: jobId,
      successMessage,
      errorMessage,
    }).then(() => {
      fetchManagementUITable()
    })
  }

  const onConfirmRestoreJobSeries = (
    successMessage: string,
    errorMessage: string,
  ) => {
    setIsDialogOpened(false)
    withPromise(restoreJobSeries, {
      job_series_id,
      successMessage,
      errorMessage,
    }).then(() => {
      fetchManagementUITable()
    })
  }

  const updateAssignees = () => {
    updateJobAssignees({
      params: {
        id: jobId,
        action: 'reassign',
      },
      additionalParams: {
        jobId,
      },
      data: updateJobAssigneesTmpData,
      successMessage: formatMessage(messages.reassignSuccessMessage),
      errorMessage: formatMessage(messages.reassignErrorMessage),
    })
    setUpdateJobAssigneesTmpData(DEFAULT_JOB_ASSIGNEES_TMP_DATA)
  }

  const onRemoveJobAssignees = (assigneeId: number, type: string) => {
    const formattedType =
      type === FILTER_TYPE_ASSIGNED_USER ? 'users' : 'user_groups'
    const data = DEFAULT_JOB_ASSIGNEES_TMP_DATA as TmpData
    data.remove[formattedType] = [
      {
        id: assigneeId,
      },
    ]

    updateJobAssignees({
      params: {
        id: jobId,
        action: 'reassign',
      },
      additionalParams: {
        jobId,
      },
      data,
      successMessage: formatMessage(messages.reassignSuccessMessage),
      errorMessage: formatMessage(messages.reassignErrorMessage),
    })
  }

  const onSelectAssignee = (
    item: { id: number; name: string },
    assigneeType?: string,
    action?: string,
  ) => {
    if (assigneeType && action) {
      setUpdateJobAssigneesTmpData({
        add: {
          ...updateJobAssigneesTmpData.add,
          [assigneeType]: [
            ...updateJobAssigneesTmpData.add[assigneeType].filter(
              ({ id }) => id !== item.id,
            ),
          ],
        },
        remove: {
          ...updateJobAssigneesTmpData.remove,
          [assigneeType]: [
            ...updateJobAssigneesTmpData.remove[assigneeType].filter(
              ({ id }) => id !== item.id,
            ),
          ],
        },
        [action]: {
          ...updateJobAssigneesTmpData[action],
          [assigneeType]: [
            ...updateJobAssigneesTmpData[action][assigneeType],
            item,
          ],
        },
      })
    }
  }

  const onDiveToJob = () => {
    window.open(`${MY_JOBS}?search=${jobNumber}&respect_filters=0`)
  }

  const renderNoGroupContent = (
    content: Array<UnitTree> | Array<UnitInsideTree>,
    groupName = '',
  ) =>
    content.map(({ id, name }, index) => (
      <span key={id}>
        {index === 0 ? groupName && ': ' : ', '}
        {name}
      </span>
    ))

  const renderUnitsContent = (
    data: { groups: Array<UnitTree>; no_groups: Array<UnitTree> },
    needs_locus: boolean,
    needs_locus_group: boolean,
  ) => {
    const { groups, no_groups } = data

    if (!groups?.length && !no_groups?.length) {
      let message = ''

      if (!needs_locus && !needs_locus_group) {
        message = '-'
      } else if (needs_locus && !needs_locus_group) {
        message = formatMessage(messages.allUnits)
      } else if (!needs_locus && needs_locus_group) {
        message = formatMessage(messages.allUnitGroups)
      } else {
        message = formatMessage(messages.allUnitsAndGroups)
      }

      return <div>{message}</div>
    }

    const groupsContent = groups.map(({ id, name, locuses }, index) => (
      <span key={id}>
        {index !== 0 && ' '}
        <GroupName>{name}</GroupName>
        {renderNoGroupContent(locuses, name)};
      </span>
    ))

    return (
      <div>
        {groupsContent}
        {renderNoGroupContent(no_groups)}
      </div>
    )
  }

  const renderTasks = () => {
    let originalTaskPosition = 0
    let currentTaskPosition: string | number = 0
    let duplicatedTaskPosition = 0

    const sortedTasks = [] as Array<Task>

    tasks.forEach(singleTask => {
      if (!singleTask.original_task_id) {
        const { id: currentTaskId } = singleTask
        const duplicates = tasks.filter(
          singleTask => singleTask.original_task_id === currentTaskId,
        )

        sortedTasks.push(singleTask)

        if (duplicates.length) {
          duplicates.forEach(item => {
            sortedTasks.push(item)
          })
        }
      }
    })

    return sortedTasks.map((task, index) => {
      const { id, position, original_task_id } = task
      const nextTaskInfo = sortedTasks[index + 1]
      const nextOriginalTaskId = nextTaskInfo && nextTaskInfo.original_task_id
      const isNextTaskDuplicable =
        nextOriginalTaskId === id && !original_task_id
      const isCurrentTaskDuplicated =
        original_task_id && originalTaskPosition === position

      if (!isCurrentTaskDuplicated) {
        currentTaskPosition = Number(Math.trunc(+currentTaskPosition)) + 1
        duplicatedTaskPosition = 0
      }

      if (isNextTaskDuplicable || isCurrentTaskDuplicated) {
        currentTaskPosition = `${Math.trunc(
          +currentTaskPosition,
        )}.${(duplicatedTaskPosition += 1)}`
      }

      originalTaskPosition = position

      return (
        <SingleTask
          key={task.id}
          task={task}
          getStatusIcon={getStatusIcon}
          position={currentTaskPosition}
          assigneeColDisplay={assigneeColDisplay}
          recurrenceColDisplay={recurrenceColDisplay}
          mediaColDisplay={mediaColDisplay}
          jobIdColDisplay={jobIdColDisplay}
          createdByColDisplay={createdByColDisplay}
          modifiedByColDisplay={modifiedByColDisplay}
          tasksCompletedColDisplay={tasksCompletedColDisplay}
          dueDateColDisplay={dueDateColDisplay}
          createdDateColDisplay={createdDateColDisplay}
          startDateColDisplay={startDateColDisplay}
          completedDateColDisplay={completedDateColDisplay}
          unitsColDisplay={unitsColDisplay}
          endDateColDisplay={endDateColDisplay}
          jobId={jobId}
          setJobContextMenuPosition={setJobContextMenuPosition}
          setIsDialogOpened={setIsDialogOpened}
          setConfirmDialogSettings={setConfirmDialogSettings}
          renderUnitsContent={renderUnitsContent}
          isJobCompleted={isCompleted}
          isSchedulingTab={isSchedulingTab}
          is_future={is_future}
        />
      )
    })
  }

  const onOpenJobContextMenu = (event: React.MouseEvent, anchor: Element) => {
    event.preventDefault()
    setJobContextMenuPosition({
      left: event.pageX,
      top: event.pageY,
    })
    setJobContextMenuAnchor(anchor)
  }

  const getRepeatByValue = () => {
    if (schedule?.repeatBy === WEEKLY) {
      return formatMessage(messages.week_s).toLowerCase()
    }

    if (schedule?.repeatBy === MONTHLY) {
      return formatMessage(messages.month_s).toLowerCase()
    }

    if (schedule?.repeatBy === DAILY) {
      return formatMessage(messages.day_s).toLowerCase()
    }

    return ''
  }

  const scheduleValue = useMemo(
    () =>
      schedule?.frequency
        ? `${formatMessage(messages.every)} ${
            schedule.frequency
          } ${getRepeatByValue()}`
        : '-',
    [schedule?.frequency, schedule?.repeatBy],
  )

  return !isLoading || alwaysVisible ? (
    <JobsWrapper>
      <SingleJobStyles
        onContextMenu={event => {
          onOpenJobContextMenu(
            event,
            document.querySelectorAll('.job-context-menu-anchor-el')[0],
          )
        }}>
        <Flex>
          <NameCol>
            <IconHolder
              role="button"
              justify="center"
              align="center"
              tabIndex={0}
              onClick={() => {
                setIsOpened(!isOpened)
              }}>
              <CollapseIconWrapper isOpened={isOpened} />
            </IconHolder>
            <Tooltip
              title={title}
              arrow
              classes={{ popper: 'management-ui-tooltip' }}>
              <Label>{title}</Label>
            </Tooltip>
          </NameCol>
          <OverdueCol>
            {overdue && (
              <OverdueLabel>{formatMessage(messages.overdue)}</OverdueLabel>
            )}
          </OverdueCol>
          <OtherCol>
            <Tooltip
              title={siteName}
              placement="left"
              arrow
              classes={{ popper: 'management-ui-tooltip' }}>
              <Label>{siteName}</Label>
            </Tooltip>
          </OtherCol>
          {isEverythingDeletedTab && (
            <>
              <OtherCol>
                <Label>
                  {getFormattedDate(
                    date_deleted,
                    `${shortDateDisplay} ${timeDisplay}`,
                  ) || '-'}
                </Label>
              </OtherCol>
              <OtherCol>
                <Label>
                  {needs_deletion_reason && deletion_reasons?.length
                    ? deletion_reasons.join(', ')
                    : '-'}
                </Label>
              </OtherCol>
              <OtherCol>
                {modified_by ? (
                  <UsersListComponent hideNoAssigneesIcon users={modified_by} />
                ) : (
                  <Label>-</Label>
                )}
              </OtherCol>
            </>
          )}
          {assigneeColDisplay && (
            <OtherCol>
              <UsersListComponent
                users={assigned_users}
                groups={assigned_user_groups}
                updateJobAssigneesTmpData={updateJobAssigneesTmpData}
                onAddUser={isAllowedToChange ? updateAssignees : undefined}
                onRemoveUser={
                  isAllowedToChange ? onRemoveJobAssignees : undefined
                }
                extraAction={onSelectAssignee}
              />
            </OtherCol>
          )}
          {tasksCompletedColDisplay && (
            <TasksCompletedCol>
              <Label>{`${tasks_completed_count}/${tasks_total_count}`}</Label>
            </TasksCompletedCol>
          )}
          {recurrenceColDisplay && (
            <Tooltip
              title={scheduleValue}
              placement="left"
              arrow
              classes={{ popper: 'management-ui-tooltip' }}>
              <RecurrenceCol>
                <Label>{scheduleValue}</Label>
              </RecurrenceCol>
            </Tooltip>
          )}
          {mediaColDisplay && (
            <MediaCol>
              {media ? (
                <>
                  <Label>{media}</Label>
                  <AttachmentIcon />
                </>
              ) : (
                '-'
              )}
            </MediaCol>
          )}
          {jobIdColDisplay && (
            <ManagementUICol>
              <Label>{jobNumber}</Label>
            </ManagementUICol>
          )}
          {createdDateColDisplay && (
            <OtherCol>
              <Label>
                {getFormattedDate(
                  created_date_time,
                  `${shortDateDisplay} ${timeDisplay}`,
                ) || '-'}
              </Label>
            </OtherCol>
          )}
          {createdByColDisplay && (
            <CreatedByCol>
              <UsersListComponent
                users={[created_by || created_by_trigger_rule]}
              />
            </CreatedByCol>
          )}
          {modifiedByColDisplay && (
            <OtherCol>
              <UsersListComponent hideNoAssigneesIcon users={modified_by} />
            </OtherCol>
          )}
          {unitsColDisplay && (
            <StyledUnitsCol justify="left" align="center">
              <Tooltip
                title={renderUnitsContent(
                  locuses_and_groups,
                  needs_locus,
                  needs_locus_group,
                )}
                placement="left"
                arrow
                classes={{ popper: 'management-ui-tooltip' }}>
                {renderUnitsContent(
                  locuses_and_groups,
                  needs_locus,
                  needs_locus_group,
                )}
              </Tooltip>
            </StyledUnitsCol>
          )}
          {startDateColDisplay && (
            <OtherCol>
              <Label>
                {getFormattedDate(
                  start_date_time,
                  `${shortDateDisplay} ${timeDisplay}`,
                ) || '-'}
              </Label>
            </OtherCol>
          )}
          {dueDateColDisplay && (
            <ManagementUICol>
              <Label>
                {getFormattedDate(
                  end_date_time,
                  `${shortDateDisplay} ${timeDisplay}`,
                ) || '-'}
              </Label>
            </ManagementUICol>
          )}
          {endDateColDisplay && (
            <OtherCol>
              <Label>
                {getFormattedDate(
                  repeat_until_date_time,
                  `${shortDateDisplay} ${timeDisplay}`,
                ) || '-'}
              </Label>
            </OtherCol>
          )}
          {completedDateColDisplay && (
            <OtherCol>
              <Label>
                {getFormattedDate(
                  completed_date_time,
                  `${shortDateDisplay} ${timeDisplay}`,
                ) || '-'}
              </Label>
            </OtherCol>
          )}
          {!!jobState && (
            <StatusIndicatorCol>
              <Tooltip
                title={
                  jobState === STATUS_NOT_STARTED
                    ? formatMessage(messages.todo)
                    : formatMessage(messages[jobState])
                }>
                <div>{getStatusIcon(jobState)}</div>
              </Tooltip>
            </StatusIndicatorCol>
          )}
          <ConfigureCol>
            <MoreOptionsIcon
              onClick={event => {
                onOpenJobContextMenu(event, event.currentTarget)
              }}>
              more_vert
            </MoreOptionsIcon>
          </ConfigureCol>
        </Flex>
        <PreviewInputValues previewInputs={previewInputs} />
      </SingleJobStyles>
      <MUIMenu
        anchorEl={jobContextMenuAnchor}
        open={!!jobContextMenuAnchor}
        onClose={() => {
          setJobContextMenuAnchor(null)
        }}>
        {isEverythingDeletedTab ? (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <>
            {canRecoverDeletedJobs && (
              <>
                <ContextMenuItem
                  onClick={() => {
                    onOpenJobDialog({
                      popupTitle: formatMessage(messages.restore),
                      confirmText: formatMessage(messages.restore),
                      popupMessage: (
                        <>
                          {formatMessage(messages.restoreJobDialogMessage)}{' '}
                          &quot;
                          <strong>{title}</strong>&quot;?
                        </>
                      ),
                      popupAction: () => () => {
                        onConfirmRestoreJob(
                          formatMessage(messages.restoreJobSuccessMessage),
                          formatMessage(messages.restoreJobErrorMessage),
                        )
                      },
                    })
                  }}>
                  <ReopenIcon />
                  {formatMessage(messages.restore)}
                </ContextMenuItem>
                {job_series_id && (
                  <ContextMenuItem
                    onClick={() => {
                      onOpenJobDialog({
                        popupTitle: formatMessage(messages.restoreSeries),
                        confirmText: formatMessage(messages.restore),
                        popupMessage: (
                          <>
                            {formatMessage(
                              messages.restoreJobSeriesDialogMessage,
                            )}{' '}
                            &quot;
                            <strong>{title}</strong>&quot;?
                          </>
                        ),
                        popupAction: () => () => {
                          onConfirmRestoreJobSeries(
                            formatMessage(
                              messages.restoreJobSeriesSuccessMessage,
                            ),
                            formatMessage(
                              messages.restoreJobSeriesErrorMessage,
                            ),
                          )
                        },
                      })
                    }}>
                    <ReopenAllIcon />
                    {formatMessage(messages.restoreSeries)}
                  </ContextMenuItem>
                )}
              </>
            )}
          </>
        ) : (
          <>
            {!isSchedulingTab && (
              <ContextMenuItemSecondary onClick={onDiveToJob}>
                <Icon>arrow_forward</Icon>
                {formatMessage(messages.openInJobs)}
              </ContextMenuItemSecondary>
            )}
            {!!job_series_id && allowedToDeleteJobSeries && (
              <ContextMenuItem
                onClick={() => {
                  onOpenJobDialog({
                    popupTitle: formatMessage(messages.deleteJobSeries),
                    popupMessage: (
                      <>
                        {formatMessage(messages.deleteJobSeriesDialogMessage)}{' '}
                        &quot;
                        <strong>{title}</strong>&quot;?
                      </>
                    ),
                    popupAction: (value: string) => () => {
                      onConfirmDeleteJob(
                        true,
                        formatMessage(messages.deleteJobSeriesSuccessMessage),
                        formatMessage(messages.deleteJobSeriesErrorMessage),
                        value,
                      )
                    },
                    needsDeletionReason: needs_deletion_reason,
                    deletionReasonType: deletion_reason_type,
                    deletionReasons: deletion_reasons,
                  })
                }}>
                <DeleteJobIcon />
                {formatMessage(messages.deleteJobSeries)}
              </ContextMenuItem>
            )}
            {allowedToDeleteJobs && (
              <ContextMenuItem
                onClick={() => {
                  onOpenJobDialog({
                    popupTitle: formatMessage(messages.delete),
                    popupMessage: (
                      <>
                        {formatMessage(messages.deleteJobDialogMessage)} &quot;
                        <strong>{title}</strong>&quot;?
                      </>
                    ),
                    popupAction: (value: string) => () => {
                      onConfirmDeleteJob(
                        false,
                        formatMessage(messages.deleteJobSuccessMessage),
                        formatMessage(messages.deleteJobErrorMessage),
                        value,
                      )
                    },
                    needsDeletionReason: needs_deletion_reason,
                    deletionReasonType: deletion_reason_type,
                    deletionReasons: deletion_reasons,
                  })
                }}>
                <DeleteJobIcon />
                {formatMessage(messages.delete)}
              </ContextMenuItem>
            )}
          </>
        )}
      </MUIMenu>
      <Collapse in={isOpened}>
        <TaskList>{renderTasks()}</TaskList>
      </Collapse>
    </JobsWrapper>
  ) : (
    <JobPlaceholder />
  )
}

export default connect(
  (state: {
    getIn: (
      path: Array<string>,
    ) => boolean & { date: string } & {
      usaDateFormat: boolean
      usaTimeFormat: boolean
    } & { [key: string]: boolean }
  }) => ({
    allowedToDeleteJobs: state.getIn([
      'profile',
      'profile',
      'grantedDeleteJobs',
    ]),
    allowedToDeleteJobSeries: state.getIn([
      'profile',
      'profile',
      'grantedDeleteJobSeries',
    ]),
    canRecoverDeletedJobs: state.getIn([
      'profile',
      'profile',
      'canRecoverDeletedJobs',
    ]),
    licensee: state.getIn(['profile', 'profile', 'licensee']),
    jobsData: state.getIn(['managementUI', 'jobsData']),
  }),
  {
    updateJobAssignees,
    deleteJob,
    restoreJob,
    restoreJobSeries,
  },
)(injectIntl(SingleJobComponent))
